import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MdAddCircle } from 'react-icons/md';
import axios from 'axios';
import AppLayout from '../../layouts/AppLayout';
import AlertasForm from './components/AlertasForm';
import AlertasTable from './components/AlertasTable';
import useAuth from '../../hooks/useAuth';
import useActiveCondominio from '../../hooks/useActiveCondominio';
import './styles/alertas.css';

const AlertasConfig = () => {
  const { user, loading: authLoading } = useAuth();
  const { activeCondominio } = useActiveCondominio();
  
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAlerta, setEditingAlerta] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const apiUrl = useMemo(() => process.env.REACT_APP_API_URL || 'http://localhost:4000', []);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Validações básicas
      if (!user?.condominios || user.condominios.length === 0) {
        setError("Nenhum condomínio disponível");
        setLoading(false);
        return;
      }

      // Carregar alertas do condomínio ativo (lazy load)
      const response = await axios.get(`${apiUrl}/api/alertas`, {
        params: { condominioId: activeCondominio }
      });
      setAlertas(response.data.dados || response.data);

      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.mensagem || err.message);
      setLoading(false);
    }
  }, [user, activeCondominio, apiUrl]);

// ✅ Carregar alertas quando user carregou OU condomínio ativo mudou (lazy load)
  useEffect(() => {
    if (authLoading) return; // Aguarde carregamento
    if (!user) return; // User não autenticado
    if (!activeCondominio) return; // Aguarda condomínio estar setado
    
    loadInitialData();
  }, [user, authLoading, activeCondominio, loadInitialData]);

  const handleCreateAlerta = async (novoAlerta) => {
    try {
      if (!activeCondominio) {
        throw new Error("Condomínio ativo não definido");
      }
      const response = await axios.post(`${apiUrl}/api/alertas`, {
        ...novoAlerta,
        condominioId: activeCondominio
      });

      const alertaCriado = response.data.dados || response.data;
      setAlertas([...alertas, alertaCriado]);
      setShowModal(false);
      return { success: true };
    } catch (err) {
      console.error('Erro ao criar alerta:', err);
      return { success: false, error: err.response?.data?.mensagem || err.message };
    }
  };

  const handleUpdateAlerta = async (id, alertaAtualizado) => {
    try {
      if (!activeCondominio) {
        throw new Error("Condomínio ativo não definido");
      }
      const response = await axios.put(`${apiUrl}/api/alertas/${id}`, {
        ...alertaAtualizado,
        condominioId: activeCondominio
      });

      const alertaAtualizadoData = response.data.dados || response.data;
      setAlertas(alertas.map(a => a.id === id ? alertaAtualizadoData : a));
      setEditingAlerta(null);
      setShowModal(false);
      return { success: true };
    } catch (err) {
      console.error('Erro ao atualizar alerta:', err);
      return { success: false, error: err.response?.data?.mensagem || err.message };
    }
  };

  const handleDeleteAlerta = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este alerta?')) {
      return;
    }

    try {
      if (!activeCondominio) {
        throw new Error("Condomínio ativo não definido");
      }
      await axios.delete(`${apiUrl}/api/alertas/${id}`, {
        params: { condominioId: activeCondominio }
      });

      setAlertas(alertas.filter(a => a.id !== id));
      if (editingAlerta?.id === id) {
        setEditingAlerta(null);
      }
    } catch (err) {
      console.error('Erro ao deletar alerta:', err);
      alert('Erro ao deletar alerta: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  const handleEditAlerta = (alerta) => {
    if (!activeCondominio) {
      alert("Condomínio ativo não está definido. Recarregue a página.");
      return;
    }
    setEditingAlerta(alerta);
    setShowModal(true);
  };

  const handleCancelEdit = () => {
    setEditingAlerta(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <AppLayout
        titulo="Gerenciar Alertas"
        cargo={user?.cargo}
        problemas={[]}
      >
        <div className="alertas-loading">Carregando...</div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout
        titulo="Gerenciar Alertas"
        cargo={user?.cargo}
        problemas={[]}
      >
        <div className="alertas-error">
          <h2>Erro ao carregar dados</h2>
          <p>{error}</p>
          <button onClick={loadInitialData} className="alertas-btn-retry">
            Tentar Novamente
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      titulo="Gerenciar Alertas"
      cargo={user?.cargo}
      problemas={[]}
    >
      <div className="alertas-container">
        {/* Header/Hero Section */}
        <div className="alertas-hero">
          <button 
            className="alertas-btn-back" 
            onClick={() => window.history.back()}
            title="Voltar"
          >
            <span className="alertas-btn-back-icon">←</span>
            <span className="alertas-btn-back-text">Voltar</span>
          </button>
          <div className="alertas-hero-content">
            <h1 className="alertas-hero-title">Gerenciar Alertas</h1>
            <p className="alertas-hero-description">
              Configure alertas automáticos para monitorar seus condomínios. Receba notificações por 
              e-mail ou Telegram sobre eventos importantes.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <section className="feature-section">
          {/* Toolbar com ações */}
          <div className="feature-section__toolbar">
            <div className="feature-section__toolbar-start">
              <h2 className="feature-section__toolbar-title">Alertas Configurados</h2>
              <span className="feature-section__toolbar-badge">
                {alertas.length} alerta{alertas.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="feature-section__toolbar-end">
              <button 
                className="feature-section__toolbar-btn-primary" 
                onClick={() => { setEditingAlerta(null); setShowModal(true); }}
              >
                <MdAddCircle className="alertas-btn-icon" />
                <span>Novo Alerta</span>
              </button>
              {/* Slot escalável: futuros botões aqui (export, filtro, etc.) */}
            </div>
          </div>

          {/* Table Section */}
          <div className="feature-section__body">
            <AlertasTable
              alertas={alertas}
              condominios={user?.condominios || []}
              onEdit={handleEditAlerta}
              onDelete={handleDeleteAlerta}
            />
          </div>
        </section>

        {showModal && (
          <div className="alertas-modal-overlay" onClick={() => { setEditingAlerta(null); setShowModal(false); }}>
            <div className="alertas-modal" onClick={(e) => e.stopPropagation()}>
              <AlertasForm
                condominios={user?.condominios || []}
                activeCondominio={activeCondominio}
                onSubmit={editingAlerta ? handleUpdateAlerta : handleCreateAlerta}
                editingAlerta={editingAlerta}
                onCancelEdit={handleCancelEdit}
              />
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default AlertasConfig;
